import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { Dialog, Button, Input } from '../../ui';
import { api } from '../api';
import ConfirmDialog from './ConfirmDialog';

/**
 * Gestión del catálogo de categorías de tipo (nombre + orden).
 * Se abre como Dialog desde la pantalla Tipos. `onChange` avisa al padre para
 * refrescar el select de categoría del formulario de tipo.
 */
export default function CategoriasManager({ open, onClose, onChange }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nuevo, setNuevo] = useState({ nombre: '', orden: '' });
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState({ nombre: '', orden: '' });

  const unwrap = (r) => (r && r.data && r.data.data) || (r && r.data) || [];

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    try { setCats(unwrap(await api.get('/categorias'))); }
    catch { setError('No se pudieron cargar las categorías'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) cargar(); }, [open, cargar]);

  const notify = () => { if (onChange) onChange(); };
  const apiError = (e, def) => setError((e && e.response && e.response.data && e.response.data.error) || def);

  const crear = async () => {
    if (!nuevo.nombre.trim()) return;
    setError('');
    try {
      await api.post('/categorias', { nombre: nuevo.nombre.trim(), orden: Number(nuevo.orden) || 0 });
      setNuevo({ nombre: '', orden: '' }); await cargar(); notify();
    } catch (e) { apiError(e, 'No se pudo crear'); }
  };

  const guardar = async (id) => {
    setError('');
    try {
      await api.put(`/categorias/${id}`, { nombre: editVal.nombre.trim(), orden: Number(editVal.orden) || 0 });
      setEditId(null); await cargar(); notify();
    } catch (e) { apiError(e, 'No se pudo guardar'); }
  };

  // Categoría pendiente de confirmar su borrado, o null. El borrado lo confirma el ConfirmDialog.
  const [porBorrar, setPorBorrar] = useState(null);
  const borrar = (id) => setPorBorrar(cats.find((c) => c.id === id) || { id });

  // El error se lanza para que lo enseñe el ConfirmDialog, que se queda abierto.
  // `api.del`, no `api.delete`: el wrapper no tiene `delete` (hasta el 21/10 esto lanzaba un TypeError
  // y salía «No se pudo eliminar» siempre).
  const confirmarBorrado = async () => {
    setError('');
    await api.del(`/categorias/${porBorrar.id}`);
    setPorBorrar(null);
    await cargar(); notify();
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      title="Categorías de tipo"
      description="Organiza los tipos del inventario. El orden controla cómo se agrupan en el menú."
      size="md"
    >
      {/* Alta */}
      <div className="flex items-end gap-2 mb-4">
        <div className="flex-1">
          <label className="block text-xs font-medium text-slate-600 mb-1">Nueva categoría</label>
          <Input value={nuevo.nombre} onChange={(e) => setNuevo((n) => ({ ...n, nombre: e.target.value }))}
                 onKeyDown={(e) => e.key === 'Enter' && crear()} placeholder="Ej. Equipos" />
        </div>
        <div className="w-20">
          <label className="block text-xs font-medium text-slate-600 mb-1">Orden</label>
          <Input type="number" min={0} value={nuevo.orden}
                 onChange={(e) => setNuevo((n) => ({ ...n, orden: e.target.value }))} placeholder="0" />
        </div>
        <Button onClick={crear} className="shrink-0"><Plus className="w-4 h-4 mr-1" />Añadir</Button>
      </div>

      {error && <p className="text-sm text-rose-600 mb-2">{error}</p>}

      {/* Lista */}
      <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-72 overflow-y-auto">
        {loading ? (
          <p className="text-sm text-slate-500 p-3">Cargando…</p>
        ) : cats.length === 0 ? (
          <p className="text-sm text-slate-500 p-3">Aún no hay categorías.</p>
        ) : cats.map((c) => (
          <div key={c.id} className="flex items-center gap-2 px-3 py-2">
            {editId === c.id ? (
              <>
                <Input className="flex-1" value={editVal.nombre}
                       onChange={(e) => setEditVal((v) => ({ ...v, nombre: e.target.value }))} />
                <Input type="number" min={0} className="w-16" value={editVal.orden}
                       onChange={(e) => setEditVal((v) => ({ ...v, orden: e.target.value }))} />
                <button onClick={() => guardar(c.id)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded" title="Guardar"><Check className="w-4 h-4" /></button>
                <button onClick={() => setEditId(null)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded" title="Cancelar"><X className="w-4 h-4" /></button>
              </>
            ) : (
              <>
                <span className="text-xs text-slate-400 w-6 text-center">{c.orden}</span>
                <span className="flex-1 text-sm text-slate-700">{c.nombre}</span>
                <button onClick={() => { setEditId(c.id); setEditVal({ nombre: c.nombre, orden: c.orden }); }}
                        className="p-1.5 text-slate-500 hover:bg-slate-100 rounded" title="Editar"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => borrar(c.id)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
              </>
            )}
          </div>
        ))}
      </div>
    </Dialog>
    <ConfirmDialog
      open={!!porBorrar}
      onClose={() => setPorBorrar(null)}
      titulo={`Eliminar la categoría «${(porBorrar && porBorrar.nombre) || ''}»`}
      textoConfirmar="Eliminar"
      destructivo
      onConfirm={confirmarBorrado}
    >
      <p>Los tipos que la usen quedarán sin categoría.</p>
    </ConfirmDialog>
    </>
  );
}
